import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChartColumn, CircleAlert, CircleCheck, Eye } from "lucide-react";
import Button from "../../components/Button/Button";
import Loader from "../../components/Loader/Loader";
import StatusChip from "../../components/StatusChip/StatusChip";
import { formatWeekLabel, getWeekRange, hasPermission, toApiDate } from "../LessonPlanner/utils/lessonPlanUtils";
import DiaryFilterBar from "./components/DiaryFilterBar";
import OverviewStatCard from "./components/OverviewStatCard";
import TeacherAvatar from "./components/TeacherAvatar";
import { useDiaryOverview } from "./hooks/useDiaryOverview";
import { getApprovalStatus, getWeeklyUpdateStatus } from "./utils/diaryUtils";
import { DIARY_PERMISSIONS, DIARY_STATUS_FILTER_OPTIONS } from "./WeeklyDiaryConstant";
import "../LessonPlanner/LessonPlanner.css";
import withDiaryAccess from "./components/withDiaryAccess";
import "./WeeklyDiary.css";

const WeeklyDiaryList = () => {
  const navigate = useNavigate();
  // Teachers only see their own diaries, so the Teacher Name column is for reviewers only
  const isReviewer = useMemo(() => hasPermission(DIARY_PERMISSIONS.REVIEW), []);
  // Teachers don't see Teacher Name or Subject; the diary page shows the subject
  const columnCount = isReviewer ? 10 : 8;
  const { rows, summary, weekLabel, status, setStatus, search, setSearch, isLoading } =
    useDiaryOverview();

  // Open the diary for the row's own week (falls back to the current week)
  const viewDiary = (row) => {
    const diaryWeek = row.weekStart || toApiDate(getWeekRange(new Date()).start);
    navigate(`/weekly-diary/${row.teacherId}/${row.subjectId}?week_start=${diaryWeek}`, {
      state: { teacherName: row.teacherName, subjectName: row.subjectName },
    });
  };

  return (
    <div className="wd-page">
      <div className="wd-intro">
        <h2>Weekly Diaries</h2>
        <p>Review and manage weekly diaries for your teachers — all in one place.</p>
      </div>

      <div className="wd-stat-grid">
        <OverviewStatCard
          value={summary.totalLessonPlans}
          label="Total lesson plans"
          caption={weekLabel || "This week"}
          captionTone="link"
          icon={BookOpen}
          iconTone="link"
        />
        <OverviewStatCard
          value={summary.diariesSubmitted}
          label="Diaries Submitted"
          caption={`${summary.submittedPercentage}% of faculty`}
          captionTone="link"
          icon={CircleCheck}
          iconTone="success"
        />
        <OverviewStatCard
          value={summary.overdue}
          label="With Overdue"
          caption="Require attention"
          captionTone="danger"
          icon={CircleAlert}
          iconTone="danger"
        />
        <OverviewStatCard
          value={`${summary.avgCompletion}%`}
          label="Avg. Completion"
          caption="Across all teachers"
          captionTone="link"
          icon={ChartColumn}
          iconTone="warning"
        />
      </div>

      <DiaryFilterBar
        statusOptions={DIARY_STATUS_FILTER_OPTIONS}
        status={status}
        onStatusChange={setStatus}
        search={search}
        onSearchChange={setSearch}
      />

      <div className="wd-table-card">
        <div className="wd-table-scroll">
          <table className="wd-table">
            <thead>
              <tr>
                {isReviewer && <th>Teacher Name</th>}
                {isReviewer && <th>Subject</th>}
                <th>Week</th>
                <th>Total</th>
                <th>Completed</th>
                <th>Flagged</th>
                <th>Due</th>
                <th>Weekly Update</th>
                <th>Principal's Approval</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={columnCount}>
                    <Loader />
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="wd-table__empty">
                    No diaries found
                  </td>
                </tr>
              )}
              {!isLoading &&
                rows.map((row) => {
                  const weeklyUpdate = getWeeklyUpdateStatus(row);
                  const approval = getApprovalStatus(row);
                  const week = row.weekStart;
                  const { start, end } = getWeekRange(week || new Date());
                  return (
                    <tr key={`${row.teacherId}-${row.subjectId}-${row.weekStart}`}>
                      {isReviewer && (
                        <td>
                          <span className="wd-teacher">
                            <TeacherAvatar name={row.teacherName} src={row.teacherAvatar} />
                            {row.teacherName}
                          </span>
                        </td>
                      )}
                      {isReviewer && <td>{row.subjectName}</td>}
                      <td>{row.weekLabel || (week ? formatWeekLabel(start, end) : "—")}</td>
                      <td>{row.total}</td>
                      <td>{row.completed}</td>
                      <td className={row.flagged > 0 ? "lp-text-danger" : ""}>{row.flagged}</td>
                      <td className={row.due > 0 ? "lp-text-danger" : ""}>{row.due}</td>
                      <td>
                        <StatusChip tone={weeklyUpdate.tone}>{weeklyUpdate.label}</StatusChip>
                      </td>
                      <td>{approval ? <StatusChip tone={approval.tone}>{approval.label}</StatusChip> : "—"}</td>
                      <td className="text-end">
                        <Button size="sm" className="wd-view-btn" iconRight={<Eye size={13} />} onClick={() => viewDiary(row)}>
                          View Diary
                        </Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default withDiaryAccess(WeeklyDiaryList);
